/**
 * 豆包视频生成节点 - 支持 Lite I2V 和 Pro Fast 模式
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './DoubaoVideoNode.css';

const apiService = new ApiService();

// 工具函数：判断数组是否相等
const arraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

// 工具函数：合并图片列表（保持原有顺序）
const mergeImageLists = (prev = [], next = []) => {
  const nextQueue = [...next];
  const reordered = [];

  prev.forEach((url) => {
    const index = nextQueue.indexOf(url);
    if (index !== -1) {
      reordered.push(url);
      nextQueue.splice(index, 1);
    }
  });

  return [...reordered, ...nextQueue];
};

// 通道模式选项
const channelOptions = [
  { value: 'lite', label: 'lite模式', appName: 'st-ai/doubao-seedance-1-0-lite-i2v-250428' },
  { value: 'pro', label: 'pro模式', appName: 'st-ai/doubao-seedance-1-0-pro-fast-251015' },
  { value: 'multi-ref', label: '多图参考', appName: 'st-ai/doubao-seedance-1-0-lite-i2v-250428' },
];

// 分辨率选项
const resolutionOptions = ['480p', '720p', '1080p'];

// 时长选项
const durationOptions = [5, 10];

// 视频比例选项
const aspectRatioOptions = [
  { value: '16:9', label: '16:9' },
  { value: '4:3', label: '4:3' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '9:16', label: '9:16' },
  { value: '21:9', label: '21:9' },
  { value: 'keep_ratio', label: '保持原比例' },
  { value: 'adaptive', label: '自适应' },
];

export const DoubaoVideoNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes); // 添加节点监听
  const { readOnly } = useProject();
  
  const [channel, setChannel] = useState(data.channel || 'lite');
  const [resolution, setResolution] = useState(data.resolution || '720p');
  const [duration, setDuration] = useState(data.duration || 5);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '16:9');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedImages, setConnectedImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    updateNodeData(id, {
      channel,
      resolution,
      duration,
      aspectRatio,
    });
  }, [id, channel, resolution, duration, aspectRatio, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = () => {
    const edges = getEdges();
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    
    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });
    
    return prompts;
  };

  // 获取连接到此节点的图片参考节点和任务节点
  const getConnectedImageNodes = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    
    console.log(`DoubaoVideoNode ${id} - 连接到图片输入的边:`, connectedEdges.length);
    
    const imageUrls = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`DoubaoVideoNode ${id} - 源节点:`, sourceNode?.type, sourceNode?.data);
      
      // 从图片参考节点获取图片
      if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`DoubaoVideoNode ${id} - 从图片参考节点获取图片:`, url);
        }
      }
      
      // 从图片展示节点获取图片
      if (sourceNode && sourceNode.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`DoubaoVideoNode ${id} - 从图片展示节点获取图片:`, url);
        }
      }
      
      // 从手绘节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`DoubaoVideoNode ${id} - 从手绘节点获取图片:`, url);
        } else if (sourceNode.data?.isLocalPreview) {
          console.warn(`DoubaoVideoNode ${id} - 手绘节点图片为本地预览，跳过`);
        }
      }
      
      // 从图片任务节点获取生成的图片（取第一张）
      if (sourceNode && sourceNode.type === 'imageTask') {
        console.log(`DoubaoVideoNode ${id} - 任务节点数据:`, {
          status: sourceNode.data?.status,
          generatedImages: sourceNode.data?.generatedImages,
          imagesCount: sourceNode.data?.generatedImages?.length
        });
        
        if (sourceNode.data?.generatedImages && sourceNode.data.generatedImages.length > 0) {
          const firstImage = sourceNode.data.generatedImages[0];
          const url = typeof firstImage === 'string' ? firstImage : firstImage.url;
          if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`DoubaoVideoNode ${id} - 从任务节点获取第一张图片:`, url);
          }
        }
      }
      
      // 从视频任务节点获取生成的视频封面
      if (sourceNode && sourceNode.type === 'videoTask') {
        if (sourceNode.data?.coverImageUrl) {
          const url = sourceNode.data.coverImageUrl;
          if (!url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`DoubaoVideoNode ${id} - 从视频任务节点获取封面:`, url);
          }
        }
      }
    });
    
    console.log(`DoubaoVideoNode ${id} - 总共获取到 ${imageUrls.length} 张图片`);
    return imageUrls;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片列表
  useEffect(() => {
    const imageUrls = getConnectedImageNodes();
    setConnectedImages((prev) => {
      const merged = mergeImageLists(prev, imageUrls);
      return arraysEqual(prev, merged) ? prev : merged;
    });
  }, [getConnectedImageNodes]);

  // 构建 content 数组
  const buildContentArray = (prompt, imageUrls) => {
    const content = [];
    
    // 1. 添加文本对象（包含参数）
    const textContent = `${prompt} --dur ${duration} --rs ${resolution} --rt ${aspectRatio} --cf false --wm false`;
    
    content.push({
      type: 'text',
      text: textContent
    });
    
    // 2. 根据通道模式添加图片对象
    if (imageUrls.length > 0) {
      if (channel === 'multi-ref') {
        // 多图参考模式：最多4张，都标记为 reference_image
        const maxImages = Math.min(imageUrls.length, 4);
        for (let i = 0; i < maxImages; i++) {
          content.push({
            type: 'image_url',
            image_url: {
              url: imageUrls[i]
            },
            role: 'reference_image'
          });
        }
      } else if (channel === 'lite') {
        // lite 首尾帧模式：第1张为首帧，第2张为尾帧（如果有）
        content.push({
          type: 'image_url',
          image_url: {
            url: imageUrls[0]
          },
          role: 'first_frame'
        });
        
        if (imageUrls.length > 1) {
          content.push({
            type: 'image_url',
            image_url: {
              url: imageUrls[1]
            },
            role: 'last_frame'
          });
        }
      } else if (channel === 'pro') {
        // pro 模式：单张图片，不需要 role
        content.push({
          type: 'image_url',
          image_url: {
            url: imageUrls[0]
          }
        });
      }
    }
    
    return content;
  };

  // 处理拖动开始
  const handleDragStart = (e, index) => {
    if (readOnly) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  // 处理拖动结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 处理拖动经过
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // 处理放置
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex || readOnly) return;

    const newImages = [...connectedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setConnectedImages(newImages);
    setDraggedIndex(null);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 验证多图参考模式下不能使用 1080p
      if (channel === 'multi-ref' && resolution === '1080p') {
        setError('多图参考模式不支持 1080p 分辨率');
        toast.error('多图参考模式不支持 1080p 分辨率，请选择 480p 或 720p');
        setIsGenerating(false);
        return;
      }
      
      // 2. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }
      
      const prompt = prompts.join('\n');
      console.log('使用提示词:', prompt);
      
      // 3. 获取连接的参考图片
      console.log('使用参考图片:', connectedImages);
      
      // 验证图片数量
      if (channel === 'multi-ref' && connectedImages.length > 4) {
        toast.warning('多图参考模式最多支持4张图片，将使用前4张');
      } else if (channel === 'lite' && connectedImages.length > 2) {
        toast.warning('lite首尾帧模式最多支持2张图片，将使用前2张');
      } else if (channel === 'pro' && connectedImages.length > 1) {
        toast.warning('pro模式只支持单张图片，将使用第一张');
      }
      
      // 4. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 5. 获取当前通道的 app_name
      const selectedChannel = channelOptions.find(c => c.value === channel);
      let appName = selectedChannel.appName;
      
      // lite模式下，根据是否有参考图选择t2v或i2v
      if (channel === 'lite') {
        if (connectedImages.length === 0) {
          // 文生视频（无参考图）
          appName = 'st-ai/doubao-seedance-1-0-lite-t2v-250428';
        } else {
          // 图生视频（有参考图）
          appName = 'st-ai/doubao-seedance-1-0-lite-i2v-250428';
        }
      }
      
      // 6. 构建请求参数
      const content = buildContentArray(prompt, connectedImages);
      
      const inputParams = {
        resolution: resolution,
        duration: duration,
        content: content
      };
      
      console.log('请求参数:', {
        app_name: appName,
        input_params: inputParams
      });
      
      // 调试：打印完整的content数组和aspectRatio值
      console.log('视频比例 aspectRatio:', aspectRatio);
      console.log('完整 content 数组:', JSON.stringify(content, null, 2));
      
      // 7. 调用豆包 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          task_type: 'coze_api',
          input_params: inputParams
        }
      );
      
      console.log('豆包任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        
        // 根据模式提示不同的信息
        let taskTypeLabel = '文生视频';
        if (connectedImages.length > 0) {
          if (channel === 'multi-ref') {
            taskTypeLabel = '多图参考视频';
          } else if (channel === 'lite') {
            taskTypeLabel = connectedImages.length > 1 ? '首尾帧视频' : '图生视频';
          } else {
            taskTypeLabel = '图生视频';
          }
        }
        
        toast.success(`${taskTypeLabel}任务已创建！`);
        
        // 8. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450,
          y: currentNode.position.y,
        };
        
        // 9. 创建 VideoTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: taskTypeLabel,
            status: 'pending',
            inputParams: inputParams,
            videoUrl: null,
          },
        };
        
        // 10. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建视频任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('豆包视频生成失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 获取图片限制提示
  const getImageLimitHint = () => {
    if (channel === 'multi-ref') {
      return '最多4张';
    } else if (channel === 'lite') {
      return '最多2张（首帧+尾帧）';
    } else {
      return '最多1张';
    }
  };

  // 获取图片限制数量
  const getImageLimit = () => {
    if (channel === 'multi-ref') {
      return 4;
    } else if (channel === 'lite') {
      return 2;
    } else {
      return 1;
    }
  };

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validImageTypes = ['imageReference', 'imageDisplay', 'drawing', 'imageTask', 'videoTask'];
      
      if (!validImageTypes.includes(sourceNode?.type)) {
        return false;
      }
      
      // 检查当前已连接的图片数量
      const currentEdges = getEdges();
      const imageEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      
      const limit = getImageLimit();
      if (imageEdgesCount >= limit) {
        toast.warning(`${channel === 'multi-ref' ? '多图参考' : channel === 'lite' ? 'lite' : 'pro'}模式最多支持${limit}张图片`);
        return false;
      }
      
      return true;
    }
    
    return true;
  }, [channel, getNode, getEdges, id]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'doubaoVideo', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <div className="DoubaoVideoNode-container">
      {/* 复制按钮 */}
      <button 
        className="DoubaoVideoNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="DoubaoVideoNode-handle DoubaoVideoNode-handle-prompt"
        isValidConnection={isValidConnection}
        style={{ top: '25%' }}
      >
        <div 
          className="DoubaoVideoNode-handle-label DoubaoVideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="DoubaoVideoNode-handle DoubaoVideoNode-handle-image"
        isValidConnection={isValidConnection}
        style={{ top: '75%' }}
      >
        <div 
          className="DoubaoVideoNode-handle-label DoubaoVideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多1张）"
        >
          图片
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="DoubaoVideoNode-handle DoubaoVideoNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="DoubaoVideoNode-title">豆包视频</div>

      {/* 内容区域 */}
      <div className="DoubaoVideoNode-wrapper">
        {/* 通道选择 */}
        <div className="DoubaoVideoNode-field">
          <label className="DoubaoVideoNode-label">通道</label>
          <div className="DoubaoVideoNode-button-group">
            {channelOptions.map((option) => (
              <button
                key={option.value}
                className={`DoubaoVideoNode-button ${channel === option.value ? 'DoubaoVideoNode-button-active' : ''}`}
                onClick={() => setChannel(option.value)}
                disabled={isGenerating || readOnly}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 分辨率选择 */}
        <div className="DoubaoVideoNode-field">
          <label className="DoubaoVideoNode-label">分辨率</label>
          <div className="DoubaoVideoNode-button-group">
            {resolutionOptions.map((res) => (
              <button
                key={res}
                className={`DoubaoVideoNode-button ${resolution === res ? 'DoubaoVideoNode-button-active' : ''} ${
                  channel === 'multi-ref' && res === '1080p' ? 'DoubaoVideoNode-button-disabled' : ''
                }`}
                onClick={() => setResolution(res)}
                disabled={isGenerating || readOnly || (channel === 'multi-ref' && res === '1080p')}
              >
                {res}
              </button>
            ))}
          </div>
          {channel === 'multi-ref' && (
            <div className="DoubaoVideoNode-hint-small">
              ⚠️ 多图参考模式不支持 1080p
            </div>
          )}
        </div>

        {/* 时长选择 */}
        <div className="DoubaoVideoNode-field">
          <label className="DoubaoVideoNode-label">时长</label>
          <div className="DoubaoVideoNode-button-group">
            {durationOptions.map((dur) => (
              <button
                key={dur}
                className={`DoubaoVideoNode-button ${duration === dur ? 'DoubaoVideoNode-button-active' : ''}`}
                onClick={() => setDuration(dur)}
                disabled={isGenerating || readOnly}
              >
                {dur}秒
              </button>
            ))}
          </div>
        </div>

        {/* 视频比例选择 */}
        <div className="DoubaoVideoNode-field">
          <label className="DoubaoVideoNode-label">视频比例</label>
          <div className="DoubaoVideoNode-aspect-ratio-grid">
            {aspectRatioOptions.map((option) => (
              <button
                key={option.value}
                className={`DoubaoVideoNode-button DoubaoVideoNode-aspect-ratio-button ${aspectRatio === option.value ? 'DoubaoVideoNode-button-active' : ''}`}
                onClick={() => setAspectRatio(option.value)}
                disabled={isGenerating || readOnly}
                title={option.value === 'keep_ratio' ? '所生成视频的宽高比与所上传图片的宽高比保持一致' : option.value === 'adaptive' ? '根据所上传图片的比例，自动选择最合适的宽高比' : ''}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 参考图片展示 */}
        <div className="DoubaoVideoNode-field">
          <label className="DoubaoVideoNode-label">
            参考图片 ({getImageLimitHint()})
          </label>
          {connectedImages.length > 0 ? (
            <div className="DoubaoVideoNode-image-grid nodrag">
              {connectedImages.map((imageUrl, index) => {
                // 判断是否超出限制
                const isOverLimit = (
                  (channel === 'multi-ref' && index >= 4) ||
                  (channel === 'lite' && index >= 2) ||
                  (channel === 'pro' && index >= 1)
                );
                
                return (
                  <div
                    key={`${imageUrl}-${index}`}
                    className={`DoubaoVideoNode-image-thumb nodrag ${
                      isOverLimit ? 'DoubaoVideoNode-image-inactive' : ''
                    } ${draggedIndex === index ? 'DoubaoVideoNode-image-dragging' : ''}`}
                    draggable={!readOnly}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <img 
                      src={imageUrl} 
                      alt={`参考图 ${index + 1}`}
                      className="DoubaoVideoNode-thumb-img"
                    />
                    <div className="DoubaoVideoNode-thumb-index">
                      {index + 1}
                    </div>
                    {!isOverLimit && channel === 'lite' && (
                      <div className="DoubaoVideoNode-thumb-role">
                        {index === 0 ? '首帧' : '尾帧'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="DoubaoVideoNode-image-placeholder">
              🖼️ 连接图片节点添加参考图片
            </div>
          )}
        </div>

        {/* 连接提示 */}
        <div className="DoubaoVideoNode-hint">
          💡 <strong>使用说明</strong><br />
          • <strong>lite模式</strong>：首尾帧控制视频轨迹<br />
          • <strong>pro模式</strong>：单图生成高质量视频<br />
          • <strong>多图参考</strong>：保持风格一致（最多4张）<br />
          点击生成后会自动创建视频任务节点
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`DoubaoVideoNode-generate ${isGenerating || readOnly ? 'DoubaoVideoNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="DoubaoVideoNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

DoubaoVideoNode.displayName = 'DoubaoVideoNode';

